import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { GridEditorState, GridEditorActions } from '../../GridEditorPresenter';
import { ErrorDisplay, SuccessMessage, WarningMessage } from '../ErrorDisplay';

interface ErrorPanelProps {
  saveErrors: GridEditorState['saveErrors'];
  lastSaveResult: GridEditorState['lastSaveResult'];
  actions: Pick<GridEditorActions, 'clearSaveErrors' | 'saveChanges'>;
}

export const ErrorPanel: React.FC<ErrorPanelProps> = ({
  saveErrors,
  lastSaveResult,
  actions
}) => {
  // Ne pas afficher le panel s'il n'y a pas d'erreurs et pas de résultat récent
  if (saveErrors.length === 0 && !lastSaveResult) {
    return null;
  }

  const handleRetry = () => {
    actions.saveChanges();
  };

  const handleDismiss = () => {
    actions.clearSaveErrors();
  };

  return (
    <div className="border-t border-slate-200 p-4 bg-slate-50">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-slate-900 flex items-center">
          <AlertTriangle className="h-4 w-4 mr-1" />
          Statut de sauvegarde
        </h3>
        <button
          onClick={handleDismiss}
          className="text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-3">
        {/* Affichage du résultat de la dernière sauvegarde */}
        {lastSaveResult && (
          <>
            {lastSaveResult.success && (
              <SuccessMessage
                message={lastSaveResult.message || "Toutes les modifications ont été sauvegardées avec succès"}
                onDismiss={handleDismiss}
              />
            )}
            
            {!lastSaveResult.success && lastSaveResult.partialSuccess && (
              <WarningMessage
                message={lastSaveResult.message || "Certaines modifications ont été sauvegardées avec succès, mais des erreurs sont survenues"}
                onRetry={handleRetry}
                onDismiss={handleDismiss}
              />
            )}
            
            {!lastSaveResult.success && !lastSaveResult.partialSuccess && (
              <ErrorDisplay
                errors={lastSaveResult.errors || []}
                onRetry={handleRetry}
                onDismiss={handleDismiss}
              />
            )}
          </>
        )}

        {/* Affichage des erreurs actuelles */}
        {saveErrors.length > 0 && (
          <ErrorDisplay
            errors={saveErrors}
            onRetry={handleRetry}
            onDismiss={handleDismiss}
          />
        )}
      </div>

      {/* Informations supplémentaires */}
      <div className="mt-3 text-xs text-slate-500">
        <p>💡 Les erreurs de validation peuvent être corrigées directement dans l'interface</p>
        <p>🔄 Les erreurs réseau peuvent être résolues en réessayant</p>
        <p>⚠️ Les erreurs d'autorisation nécessitent une intervention administrateur</p>
      </div>
    </div>
  );
};
